import type {
  ActualVsBudgetCompanyFixture,
  ActualVsBudgetMetricFixture,
  ActualVsBudgetMetricKey,
} from '@/features/actual-vs-budget/types'
import {
  getClosedFixtureMonths,
  getFixtureVariance,
  getFixtureVariancePercent,
  roundFixtureValue,
  sumCompleteFixtureValues,
} from '@/features/actual-vs-budget/fixture-financials'

export type PortfolioStatus =
  | 'favorable'
  | 'watch'
  | 'unfavorable'
  | 'neutral'
  | 'unavailable'

export const portfolioSortOptions = [
  { value: 'attention', label: 'Needs attention' },
  { value: 'company-asc', label: 'Company A–Z' },
  { value: 'revenue-fytd', label: 'Revenue FYTD variance' },
  { value: 'adjusted-ebitda-fytd', label: 'EBITDA FYTD variance' },
  { value: 'revenue-forecast', label: 'Revenue forecast variance' },
  {
    value: 'adjusted-ebitda-forecast',
    label: 'EBITDA forecast variance',
  },
] as const

export type PortfolioSort = (typeof portfolioSortOptions)[number]['value']

export function isPortfolioSort(value: string): value is PortfolioSort {
  return portfolioSortOptions.some((option) => option.value === value)
}

export interface PortfolioComparison {
  actual: number | null
  budget: number | null
  varianceAmount: number | null
  variancePercent: number | null
  status: PortfolioStatus
}

export interface PortfolioOutlook {
  forecast: number | null
  budget: number | null
  varianceAmount: number | null
  variancePercent: number | null
  status: PortfolioStatus
}

export interface PortfolioMetricSummary {
  key: ActualVsBudgetMetricKey
  label: string
  unit: ActualVsBudgetMetricFixture['unit']
  fytd: PortfolioComparison
  fullYearOutlook: PortfolioOutlook
}

export interface PortfolioRow {
  id: string
  name: string
  fund: string
  fiscalYearLabel: string
  periodLabel: string
  metrics: Record<ActualVsBudgetMetricKey, PortfolioMetricSummary>
}

export interface PortfolioSelection {
  query: string
  fund: string
  visibleMetrics: readonly ActualVsBudgetMetricKey[]
  sort: PortfolioSort
}

// GS-12 explicitly permits local synthetic calculations for this isolated mock.
// Production financial calculations remain backend-owned.
export function getPortfolioStatus(
  variancePercent: number | null,
  unit:
    | ActualVsBudgetMetricFixture['unit']
    | 'percentage-points' = 'usd-millions'
): PortfolioStatus {
  if (variancePercent === null) return 'unavailable'
  if (unit === 'percentage-points') return 'neutral'
  if (variancePercent >= -0.02) return 'favorable'
  if (variancePercent > -0.1) return 'watch'
  return 'unfavorable'
}

function buildMetricSummary(
  company: ActualVsBudgetCompanyFixture,
  metric: ActualVsBudgetMetricFixture
): PortfolioMetricSummary {
  const { closedMonths } = getClosedFixtureMonths(company, metric)
  const actual = sumCompleteFixtureValues(
    closedMonths.map(({ actual }) => actual)
  )
  const budget = sumCompleteFixtureValues(
    closedMonths.map(({ budget }) => budget)
  )
  const varianceAmount = getFixtureVariance(actual, budget)
  const variancePercent = getFixtureVariancePercent(actual, budget)
  const fullYearForecast =
    metric.fullYearForecast === null
      ? null
      : roundFixtureValue(metric.fullYearForecast, 1)
  const fullYearBudget =
    metric.fullYearBudget === null
      ? null
      : roundFixtureValue(metric.fullYearBudget, 1)
  const outlookVariancePercent = getFixtureVariancePercent(
    fullYearForecast,
    fullYearBudget
  )
  const outlookVarianceAmount = getFixtureVariance(
    fullYearForecast,
    fullYearBudget
  )

  return {
    key: metric.key,
    label: metric.label,
    unit: metric.unit,
    fytd: {
      actual,
      budget,
      varianceAmount,
      variancePercent,
      status: getPortfolioStatus(variancePercent, metric.unit),
    },
    fullYearOutlook: {
      forecast: fullYearForecast,
      budget: fullYearBudget,
      varianceAmount: outlookVarianceAmount,
      variancePercent: outlookVariancePercent,
      status: getPortfolioStatus(outlookVariancePercent, metric.unit),
    },
  }
}

function getMetric(
  company: ActualVsBudgetCompanyFixture,
  key: ActualVsBudgetMetricKey
) {
  const metric = company.metrics.find((candidate) => candidate.key === key)

  if (!metric) {
    throw new Error(`Missing ${key} fixture for ${company.id}`)
  }

  return buildMetricSummary(company, metric)
}

function formatPeriodLabel(company: ActualVsBudgetCompanyFixture) {
  const latestClosedDate = new Date(
    `${company.latestClosedPeriodEnd}T00:00:00Z`
  )
  const closedPeriod = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(latestClosedDate)
  const fiscalYearEnd = new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(
    new Date(
      Date.UTC(
        latestClosedDate.getUTCFullYear(),
        company.fiscalYearEnd.month - 1,
        company.fiscalYearEnd.day
      )
    )
  )

  return `FYTD through ${closedPeriod} · FY ends ${fiscalYearEnd}`
}

export function buildPortfolioRows(
  companies: readonly ActualVsBudgetCompanyFixture[]
): PortfolioRow[] {
  return companies.map((company) => ({
    id: company.id,
    name: company.name,
    fund: company.fund,
    fiscalYearLabel: company.fiscalYearLabel,
    periodLabel: formatPeriodLabel(company),
    metrics: {
      revenue: getMetric(company, 'revenue'),
      'adjusted-ebitda': getMetric(company, 'adjusted-ebitda'),
    },
  }))
}

function getSortValue(
  row: PortfolioRow,
  sort: Exclude<PortfolioSort, 'attention' | 'company-asc'>
) {
  if (sort === 'revenue-fytd') {
    return row.metrics.revenue.fytd.variancePercent
  }
  if (sort === 'adjusted-ebitda-fytd') {
    return row.metrics['adjusted-ebitda'].fytd.variancePercent
  }
  if (sort === 'revenue-forecast') {
    return row.metrics.revenue.fullYearOutlook.variancePercent
  }
  return row.metrics['adjusted-ebitda'].fullYearOutlook.variancePercent
}

function compareNullableValues(
  left: number | null,
  right: number | null,
  leftName: string,
  rightName: string
) {
  if (left === null && right === null) return leftName.localeCompare(rightName)
  if (left === null) return 1
  if (right === null) return -1
  return left - right || leftName.localeCompare(rightName)
}

function getAttentionValue(
  row: PortfolioRow,
  visibleMetrics: readonly ActualVsBudgetMetricKey[]
) {
  const values = visibleMetrics.flatMap((key) => {
    const metric = row.metrics[key]
    if (metric.unit !== 'usd-millions') return []

    return [
      metric.fytd.variancePercent,
      metric.fullYearOutlook.variancePercent,
    ].filter((value): value is number => value !== null)
  })

  return values.length === 0 ? null : Math.min(...values)
}

export function selectPortfolioRows(
  rows: readonly PortfolioRow[],
  selection: PortfolioSelection
) {
  const query = selection.query.trim().toLocaleLowerCase()
  const selected = rows.filter(
    (row) =>
      (query.length === 0 || row.name.toLocaleLowerCase().includes(query)) &&
      (selection.fund === 'all' || row.fund === selection.fund)
  )

  return [...selected].sort((left, right) => {
    if (selection.sort === 'company-asc') {
      return left.name.localeCompare(right.name)
    }
    if (selection.sort === 'attention') {
      return compareNullableValues(
        getAttentionValue(left, selection.visibleMetrics),
        getAttentionValue(right, selection.visibleMetrics),
        left.name,
        right.name
      )
    }

    return compareNullableValues(
      getSortValue(left, selection.sort),
      getSortValue(right, selection.sort),
      left.name,
      right.name
    )
  })
}
